// Daily briefing reminder via @capacitor/local-notifications. Purely
// on-device scheduling — no push infrastructure, no server involvement.
// Same discipline as health.js: dynamic import, every native call
// time-boxed, and the plugin Proxy is never awaited or returned bare
// (thenable-proxy trap — see shared-docs/capacitor-native-setup.md).

import { isNative } from '../utils/platform';

// Fixed id so re-scheduling always replaces the previous reminder.
const REMINDER_ID = 700001;

function withTimeout(promise, ms, fallback) {
  return Promise.race([
    Promise.resolve(promise).catch(() => fallback),
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

// Resolves { api } or null on web / load failure.
async function plugin() {
  if (!isNative()) return null;
  try {
    const TIMED_OUT = Symbol('timeout');
    const mod = await Promise.race([
      import('@capacitor/local-notifications'),
      new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), 6000)),
    ]);
    if (mod === TIMED_OUT || !mod?.LocalNotifications) return null;
    return { api: mod.LocalNotifications };
  } catch {
    return null;
  }
}

export function notificationsSupported() {
  return isNative();
}

// 'HH:MM' → { hour, minute } (null when unparsable).
function parseTime(briefingTime) {
  const m = /^(\d{1,2}):(\d{2})$/.exec((briefingTime || '').trim());
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

// Schedules (or replaces) the repeating daily reminder at the user's briefing
// time. Requests permission on first use. Returns { ok, error } — never
// throws, so the Settings toggle can surface the real reason.
export async function scheduleBriefingReminder(briefingTime) {
  const p = await plugin();
  if (!p) return { ok: false, error: 'not-native' };
  const time = parseTime(briefingTime) || { hour: 7, minute: 30 };

  try {
    let perm = await withTimeout(p.api.checkPermissions(), 4000, null);
    if (perm?.display !== 'granted') {
      // The permission sheet needs however long the user takes — generous box.
      perm = await withTimeout(p.api.requestPermissions(), 60000, null);
    }
    if (perm?.display !== 'granted') {
      return { ok: false, error: 'permission-denied' };
    }

    await withTimeout(p.api.cancel({ notifications: [{ id: REMINDER_ID }] }), 4000, null);
    await withTimeout(p.api.schedule({
      notifications: [{
        id: REMINDER_ID,
        // The reminder fires at whatever hour the user chose — the copy must
        // work at 7am and 9pm alike.
        title: 'Time to check in',
        body: 'Your Cadence Check-In is ready — tap to hear your day.',
        schedule: { on: { hour: time.hour, minute: time.minute }, allowWhileIdle: true },
      }],
    }), 6000, null);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }
}

export async function cancelBriefingReminder() {
  const p = await plugin();
  if (!p) return;
  await withTimeout(p.api.cancel({ notifications: [{ id: REMINDER_ID }] }), 4000, null);
}

// Whether the reminder is actually pending with the OS (survives app
// restarts; the source of truth the Settings toggle reflects).
export async function isBriefingReminderScheduled() {
  const p = await plugin();
  if (!p) return false;
  const pending = await withTimeout(p.api.getPending(), 4000, null);
  return (pending?.notifications || []).some((n) => Number(n.id) === REMINDER_ID);
}
